import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { ResizeOptions } from '../model';
import { resizeImage } from '../resize/resize-image';
import { CAPTURE_TELEMETRY } from '../telemetry/capture-telemetry';
import { TorchUnavailableReason } from './torch-capture-copy';
import { TorchCaptureBindings, TorchCaptureState, TorchCaptureStatus } from './torch-capture.model';
import { MediaDevicesGateway } from './media-devices.gateway';
import { closeCamera, grabFrame, PhotoCapture, setTorch, supportsTorch } from './torch-capture';
import {
  clampZoom,
  currentHardwareZoom,
  DIGITAL_ZOOM_RANGE,
  hardwareZoomRange,
  setHardwareZoom,
  ZoomMode,
  ZoomRange
} from './torch-zoom';

type CaptureMethod = 'photo' | 'frame';

interface CapturedFrame {
  readonly image: string;
  readonly method: CaptureMethod;
}

class TorchCameraNotLoadedError extends Error {
  constructor() {
    super('TorchCameraService.load() must be called before the camera opens');
    this.name = 'TorchCameraNotLoadedError';
  }
}

@Injectable()
export class TorchCameraService {
  private readonly devices = inject(MediaDevicesGateway);
  private readonly telemetry = inject(CAPTURE_TELEMETRY);

  private readonly status = signal<TorchCaptureStatus>('idle');
  private readonly torchAvailable = signal(false);
  private readonly torchOn = signal(false);
  private readonly zoom = signal(DIGITAL_ZOOM_RANGE.min);
  private readonly zoomMode = signal<ZoomMode>('digital');
  private readonly zoomRange = signal<ZoomRange>(DIGITAL_ZOOM_RANGE);

  public readonly state: Signal<TorchCaptureState> = computed(() => ({
    status: this.status(),
    torchAvailable: this.torchAvailable(),
    torchOn: this.torchOn(),
    zoom: this.zoom(),
    zoomMode: this.zoomMode(),
    zoomRange: this.zoomRange()
  }));

  private bindings: TorchCaptureBindings | null = null;
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private photoCapture: PhotoCapture | null = null;
  private photoModeReady = false;
  private destroyed = false;
  private zoomApplying: Promise<void> | null = null;
  private pendingZoom: number | null = null;

  constructor() {
    const stopWatchingVisibility = this.devices.onPageHidden(() => this.closeOnPageHidden());
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      stopWatchingVisibility();
      this.stop();
    });
  }

  public load(bindings: TorchCaptureBindings): void {
    this.bindings = bindings;
  }

  public async open(video: HTMLVideoElement): Promise<void> {
    this.requireBindings();
    if (this.status() !== 'idle') {
      return;
    }

    if (!this.devices.isCameraAvailable()) {
      this.reportUnavailable('no-camera');
      return;
    }

    this.status.set('starting');
    this.telemetry.recordCaptureEvent('capture_started', { mode: 'torch' });

    let stream: MediaStream;
    try {
      stream = await this.devices.openRearCamera();
    } catch (error) {
      this.reportUnavailable(this.reasonFor(error));
      return;
    }

    if (this.destroyed) {
      closeCamera(stream);
      return;
    }
    this.stream = stream;
    this.video = video;

    video.srcObject = stream;
    const ready = await this.devices.waitForCameraReady(video);
    if (this.isStale(stream)) {
      this.stop();
      return;
    }
    if (!ready) {
      this.reportUnavailable('camera-error');
      return;
    }

    const track = stream.getVideoTracks()[0];
    this.torchAvailable.set(!!track && supportsTorch(track));
    this.setUpZoom(track);
    this.photoCapture = track ? this.devices.createPhotoCapture(track) : null;

    await this.preparePhotoMode();
    if (this.isStale(stream)) {
      this.stop();
      return;
    }

    this.status.set('live');
  }

  public async toggleTorch(): Promise<void> {
    const stream = this.stream;
    if (!stream || this.status() !== 'live') {
      return;
    }

    if (this.torchOn()) {
      await this.applyTorch(false);
      return;
    }

    this.status.set('preparing');
    await this.preparePhotoMode();
    if (this.isStale(stream)) {
      return;
    }
    await this.applyTorch(true);
    if (this.torchOn()) {
      await this.keepTorchOn(stream);
    }
    if (this.isStale(stream)) {
      return;
    }
    this.status.set('live');
  }

  public async capture(): Promise<void> {
    const video = this.video;
    const stream = this.stream;
    if (!video || !stream || this.status() !== 'live') {
      return;
    }

    this.status.set('capturing');
    video.pause();
    const torchOn = this.torchOn();

    let frame: CapturedFrame;
    try {
      frame = await this.cropToDigitalZoom(await this.takeFrame(video));
    } catch {
      if (!this.isStale(stream)) {
        this.reportUnavailable('camera-error');
      }
      return;
    }

    if (this.isStale(stream)) {
      return;
    }

    this.telemetry.recordCaptureEvent('capture_completed', { mode: 'torch', torchOn, method: frame.method });
    this.stop();
    this.requireBindings().outputs.captured.emit(frame.image);
  }

  public cancel(): void {
    this.stop();
    this.requireBindings().outputs.cancelled.emit();
  }

  public setZoom(level: number): void {
    if (this.status() !== 'live') {
      return;
    }
    const zoom = clampZoom(level, this.zoomRange());
    if (zoom === this.zoom()) {
      return;
    }
    this.zoom.set(zoom);
    if (this.zoomMode() === 'hardware') {
      this.requestHardwareZoom(zoom);
    }
  }

  private setUpZoom(track: MediaStreamTrack | undefined): void {
    const hardwareRange = track ? hardwareZoomRange(track) : null;
    if (track && hardwareRange) {
      this.zoomMode.set('hardware');
      this.zoomRange.set(hardwareRange);
      this.zoom.set(currentHardwareZoom(track, hardwareRange));
      return;
    }
    this.useDigitalZoom();
  }

  private useDigitalZoom(): void {
    this.zoomMode.set('digital');
    this.zoomRange.set(DIGITAL_ZOOM_RANGE);
    this.zoom.set(DIGITAL_ZOOM_RANGE.min);
  }

  private requestHardwareZoom(level: number): void {
    this.pendingZoom = level;
    this.zoomApplying ??= this.applyPendingZoom();
  }

  private async applyPendingZoom(): Promise<void> {
    while (this.pendingZoom !== null) {
      const level = this.pendingZoom;
      this.pendingZoom = null;
      const track = this.stream?.getVideoTracks()[0];
      if (!track) {
        break;
      }
      try {
        await setHardwareZoom(track, level);
      } catch {
        this.pendingZoom = null;
        this.useDigitalZoom();
      }
    }
    this.zoomApplying = null;
  }

  private async cropToDigitalZoom(frame: CapturedFrame): Promise<CapturedFrame> {
    if (this.zoomMode() !== 'digital' || this.zoom() <= DIGITAL_ZOOM_RANGE.min) {
      return frame;
    }
    const quality = this.requireBindings().resize().quality;
    return { ...frame, image: await this.devices.cropToZoom(frame.image, this.zoom(), quality) };
  }

  private closeOnPageHidden(): void {
    if (this.status() !== 'idle') {
      this.cancel();
    }
  }

  private async preparePhotoMode(): Promise<void> {
    if (!this.photoCapture || this.photoModeReady) {
      return;
    }
    try {
      await this.photoCapture.takePhoto();
      this.photoModeReady = true;
    } catch {
      this.photoCapture = null;
    }
  }

  private async takeFrame(video: HTMLVideoElement): Promise<CapturedFrame> {
    const resize = this.requireBindings().resize();
    await this.preparePhotoMode();
    const photo = this.photoCapture ? await this.takePhoto(this.photoCapture, resize) : null;
    return photo ?? { image: grabFrame(video, resize, this.devices.isDevicePortrait()), method: 'frame' };
  }

  private async takePhoto(photoCapture: PhotoCapture, resize: ResizeOptions): Promise<CapturedFrame | null> {
    try {
      const blob = await photoCapture.takePhoto();
      return { image: await resizeImage(blob, resize), method: 'photo' };
    } catch {
      return null;
    }
  }

  private async applyTorch(on: boolean): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) {
      return;
    }

    try {
      await setTorch(track, on);
      this.torchOn.set(on);
      this.telemetry.recordCaptureEvent('capture_torch_toggled', { torchOn: on });
    } catch {
      this.torchAvailable.set(false);
      this.torchOn.set(false);
    }
  }

  private async keepTorchOn(stream: MediaStream): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track) {
      return;
    }
    try {
      await this.devices.keepTorchOn(track);
    } catch {
      this.torchAvailable.set(false);
      this.torchOn.set(false);
    }
  }

  private isStale(stream: MediaStream): boolean {
    return this.destroyed || this.stream !== stream;
  }

  private reportUnavailable(reason: TorchUnavailableReason): void {
    this.telemetry.recordCaptureEvent('capture_fallback', { reason });
    this.stop();
    if (!this.destroyed) {
      this.requireBindings().outputs.unavailable.emit(reason);
    }
  }

  private stop(): void {
    const stream = this.stream;
    this.stream = null;
    this.photoCapture = null;
    this.photoModeReady = false;
    this.status.set('idle');
    this.torchOn.set(false);
    this.torchAvailable.set(false);
    this.pendingZoom = null;
    this.useDigitalZoom();

    if (this.video) {
      this.video.srcObject = null;
    }
    if (stream) {
      closeCamera(stream);
    }
  }

  private requireBindings(): TorchCaptureBindings {
    if (!this.bindings) {
      throw new TorchCameraNotLoadedError();
    }
    return this.bindings;
  }

  private reasonFor(error: unknown): TorchUnavailableReason {
    const name = (error as { name?: string })?.name;
    return name === 'NotAllowedError' || name === 'SecurityError' ? 'permission-denied' : 'camera-error';
  }
}
